import React from 'react';

import { useIntl } from '../../../../util/reactIntl';
import { xenforoDirectMessagesInboxUrl } from '../../../../util/xenforoUrls';
import { ExternalLink } from '../../../../components';

import { EnvelopeIcon } from '../MobileBottomNav/MobileBottomNavIcons';

import css from './ForumMessagesLink.module.css';

/**
 * Badge text for unread forum DMs. Hidden when there are none; capped at 99+.
 *
 * @param {number|null|undefined} count
 * @returns {string|null}
 */
export const formatForumUnreadBadge = count => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n > 99 ? '99+' : String(Math.floor(n));
};

/**
 * Accessible name for the forum Messages control (icon, bottom tab, or hamburger row).
 *
 * @param {Object} intl
 * @param {function} intl.formatMessage
 * @param {number|null|undefined} unreadCount
 * @returns {string}
 */
export const forumMessagesAriaLabel = (intl, unreadCount) => {
  const n = Number(unreadCount);
  return Number.isFinite(n) && n > 0
    ? intl.formatMessage({ id: 'ForumMessagesLink.unread' }, { count: n })
    : intl.formatMessage({ id: 'ForumMessagesLink.messages' });
};

/**
 * New-tab link to the XenForo DM inbox with an optional unread badge.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.iconWrapClassName]
 * @param {string} [props.badgeClassName]
 * @param {number} [props.unreadCount]
 * @param {number} [props.iconSize]
 * @param {React.ReactNode} [props.children]
 * @returns {JSX.Element}
 */
const ForumMessagesLink = props => {
  const {
    className,
    iconWrapClassName,
    badgeClassName,
    unreadCount = 0,
    iconSize = 24,
    children,
  } = props;
  const intl = useIntl();
  const badge = formatForumUnreadBadge(unreadCount);
  const label = forumMessagesAriaLabel(intl, unreadCount);

  return (
    <ExternalLink
      href={xenforoDirectMessagesInboxUrl()}
      className={className}
      title={label}
      aria-label={label}
    >
      <span className={iconWrapClassName || css.iconWrap}>
        <EnvelopeIcon size={iconSize} />
        {badge ? (
          <span className={badgeClassName || css.badge} aria-hidden="true">
            {badge}
          </span>
        ) : null}
      </span>
      {children}
    </ExternalLink>
  );
};

export default ForumMessagesLink;
